import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  onBackdropClick?: () => void;
  /**
   * Ancho del contenedor. El default (`max-w-3xl`) sirve para los modales de
   * formulario; el screening del familiar mete un wizard entero adentro y a esa
   * medida no entra.
   */
  widthClass?: string;
}

export function ModalShell({
  open,
  onClose,
  children,
  ariaLabelledBy,
  ariaDescribedBy,
  onBackdropClick,
  widthClass = "max-w-3xl",
}: ModalShellProps): ReactNode {
  const dialogRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (): void => {
    if (onBackdropClick) {
      onBackdropClick();
    } else {
      onClose();
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 animate-fade-in"
    >
      {/* F5-R1a — vestido del brand: backdrop navy al 40 (el de ListModal /
          NavLayer), radio 14 y la sombra literal del spec. API de props
          EXACTA — lo comparten agente, admin y uploader. */}
      <div
        className="absolute inset-0 bg-nav-navy/40"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className={`relative max-h-[85vh] w-full ${widthClass} flex flex-col rounded-[14px] bg-white overflow-hidden`}
        style={{ boxShadow: '0 24px 60px rgba(18,43,68,.14)' }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
